/**
 * React hook for real-time Firebase listener on schedules
 * Feature: 004-docs-4-schedulebuilder - Schedule Builder
 *
 * Follows the same pattern as useCurrentAction.ts
 */

import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import {
  Schedule,
  ScheduleStep,
  ScheduleIcon,
  ScheduleRewards,
  ScheduleSettings,
  ScheduleRecurrence,
  ScheduleType,
  DisplayMode,
  WeekReference,
  WeeklyScheduleDays,
  WeeklyDayConfig,
} from '../types/schedule';

/**
 * Convert Firebase schedule data to Schedule interface
 */
function firebaseScheduleToModel(
  scheduleId: string,
  data: Record<string, any>
): Schedule {
  // Convert steps map to sorted array
  const stepsData = data.steps || {};
  const steps: ScheduleStep[] = Object.entries(stepsData).map(
    ([stepId, stepData]: [string, any]) => ({
      id: stepId,
      order: stepData.order ?? 0,
      title: stepData.title,
      subtitle: stepData.subtitle ?? null,
      stepType: stepData.stepType ?? 'action',
      actionId: stepData.actionId ?? null,
      scheduleId: stepData.scheduleId ?? null,
      scheduledTime: stepData.scheduledTime ?? null,
      timer: stepData.timer ?? null,
      isSkippable: stepData.isSkippable ?? true,
      completionAction: stepData.completionAction ?? 'tap',
      helpPrompt: stepData.helpPrompt ?? null,
      tokenValue: stepData.tokenValue ?? 0,
      isRewardStep: stepData.isRewardStep ?? false,
      createdAt: stepData.createdAt,
      updatedAt: stepData.updatedAt,
    })
  );

  // Sort steps by order
  steps.sort((a, b) => a.order - b.order);

  // Build icon with defaults
  const icon: ScheduleIcon = data.icon || {
    type: 'emoji',
    emoji: '📋',
  };

  // Build rewards with defaults
  const rewards: ScheduleRewards = {
    baseTokens: data.rewards?.baseTokens ?? 0,
    bonusUnderTimeSeconds: data.rewards?.bonusUnderTimeSeconds ?? null,
    bonusUnderTimeTokens: data.rewards?.bonusUnderTimeTokens ?? null,
    bonusNoSkipsTokens: data.rewards?.bonusNoSkipsTokens ?? null,
  };

  // Build settings with defaults
  const settings: ScheduleSettings = {
    allowSkipSteps: data.settings?.allowSkipSteps ?? false,
    requireCompletionConfirmation: data.settings?.requireCompletionConfirmation ?? true,
    autoAdvanceDelayMs: data.settings?.autoAdvanceDelayMs ?? null,
    celebrationLevel: data.settings?.celebrationLevel ?? 'normal',
    routineVisualization: data.settings?.routineVisualization ?? null,
  };

  // Build recurrence (can be null)
  const recurrence: ScheduleRecurrence | null = data.recurrence
    ? {
        type: data.recurrence.type ?? 'manual',
        daysOfWeek: data.recurrence.daysOfWeek ?? null,
        startTime: data.recurrence.startTime ?? null,
        endTime: data.recurrence.endTime ?? null,
        startDate: data.recurrence.startDate ?? null,
        endDate: data.recurrence.endDate ?? null,
        notifyBeforeMinutes: data.recurrence.notifyBeforeMinutes ?? null,
      }
    : null;

  // Build weekly schedule specific fields
  const weekReference: WeekReference | null = data.weekReference
    ? {
        type: data.weekReference.type ?? 'recurring',
        weekStartDate: data.weekReference.weekStartDate ?? null,
      }
    : null;

  // Build days for weekly schedules
  let days: WeeklyScheduleDays | null = null;
  if (data.days) {
    days = {};
    const dayKeys = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
    for (const dayKey of dayKeys) {
      if (data.days[dayKey]) {
        const dayData = data.days[dayKey];
        const dayConfig: WeeklyDayConfig = {
          isEnabled: dayData.isEnabled ?? true,
          steps: dayData.steps ?? null,
          overrideSettings: dayData.overrideSettings ?? null,
        };
        days[dayKey] = dayConfig;
      }
    }
  }

  return {
    id: scheduleId,
    type: data.type as ScheduleType,
    name: data.name,
    description: data.description ?? null,
    icon,
    displayMode: (data.displayMode as DisplayMode) ?? 'single_focus',
    estimatedDurationSeconds: data.estimatedDurationSeconds ?? null,
    rewards,
    settings,
    recurrence,
    steps: steps.length > 0 ? steps : null,
    isActive: data.isActive ?? true,
    isTemplate: data.isTemplate ?? false,
    sortOrder: data.sortOrder ?? 0,
    createdBy: data.createdBy,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    // Weekly schedule specific fields
    weekReference,
    days,
  };
}

export interface UseSchedulesResult {
  schedules: Schedule[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to subscribe to schedules changes in Firebase
 *
 * @param deviceId - The device ID to monitor
 * @returns Object with schedules array, loading state, and error state
 */
export function useSchedules(deviceId: string): UseSchedulesResult {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!deviceId) {
      setLoading(false);
      setSchedules([]);
      return;
    }

    const schedulesRef = ref(database, `/devices/${deviceId}/schedules`);

    // Subscribe to real-time updates
    const unsubscribe = onValue(
      schedulesRef,
      (snapshot) => {
        setLoading(false);
        setError(null);

        if (snapshot.exists()) {
          const data = snapshot.val() as Record<string, any>;
          const schedulesArray = Object.entries(data).map(
            ([scheduleId, scheduleData]) =>
              firebaseScheduleToModel(scheduleId, scheduleData as Record<string, any>)
          );

          // Sort by sortOrder
          schedulesArray.sort((a, b) => a.sortOrder - b.sortOrder);

          setSchedules(schedulesArray);
        } else {
          setSchedules([]);
        }
      },
      (err) => {
        setLoading(false);
        setError(err as Error);
        console.error('Error listening to schedules:', err);
      }
    );

    // Cleanup on unmount - CRITICAL for preventing memory leaks
    return () => unsubscribe();
  }, [deviceId]);

  return { schedules, loading, error };
}

/**
 * Hook to subscribe to a single schedule's changes in Firebase
 *
 * @param deviceId - The device ID
 * @param scheduleId - The schedule ID to monitor
 * @returns Object with schedule data, loading state, and error state
 */
export function useSchedule(
  deviceId: string,
  scheduleId: string
): {
  schedule: Schedule | null;
  loading: boolean;
  error: Error | null;
} {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!deviceId || !scheduleId) {
      setLoading(false);
      setSchedule(null);
      return;
    }

    const scheduleRef = ref(database, `/devices/${deviceId}/schedules/${scheduleId}`);

    // Subscribe to real-time updates
    const unsubscribe = onValue(
      scheduleRef,
      (snapshot) => {
        setLoading(false);
        setError(null);

        if (snapshot.exists()) {
          const data = snapshot.val() as Record<string, any>;
          setSchedule(firebaseScheduleToModel(scheduleId, data));
        } else {
          setSchedule(null);
        }
      },
      (err) => {
        setLoading(false);
        setError(err as Error);
        console.error('Error listening to schedule:', err);
      }
    );

    // Cleanup on unmount
    return () => unsubscribe();
  }, [deviceId, scheduleId]);

  return { schedule, loading, error };
}
